// Google Analytics 4 및 Google Ads 초기화 및 이벤트 추적 유틸리티

// GA4 Measurement ID 가져오기
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID;
// Google Ads Conversion ID
const GOOGLE_ADS_ID = 'AW-17348870922';

// Google Analytics 및 Google Ads 스크립트 로드
export const initGA = () => {
  // 디버그 모드 활성화 (개발 환경에서)
  if (process.env.NODE_ENV === 'development') {
    window.gtag_debug_mode = true;
  }
  
  // gtag 함수가 이미 있는지 확인 (index.html에서 로드된 경우)
  if (window.gtag) {
    console.log('Google 태그가 이미 로드되었습니다.');
    console.log('GA4 ID:', GA_MEASUREMENT_ID);
    console.log('Google Ads ID:', GOOGLE_ADS_ID);
    
    // GA4 설정 추가 (있는 경우)
    if (GA_MEASUREMENT_ID && GA_MEASUREMENT_ID !== 'YOUR_GA4_MEASUREMENT_ID') {
      window.gtag('config', GA_MEASUREMENT_ID, {
        send_page_view: true,
        cookie_flags: 'SameSite=None;Secure',
        debug_mode: process.env.NODE_ENV === 'development'
      });
      console.log('GA4 구성 완료');
    }
    return;
  }

  // Google tag (gtag.js) 스크립트 추가 (없는 경우)
  if (!document.querySelector(`script[src*="gtag/js"]`)) {
    const script1 = document.createElement('script');
    script1.async = true;
    script1.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`;
    document.head.appendChild(script1);
  }

  // gtag 함수 초기화
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() {
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  
  // Google Ads 설정
  window.gtag('config', GOOGLE_ADS_ID);
  
  // GA4 설정 (있는 경우)
  if (GA_MEASUREMENT_ID && GA_MEASUREMENT_ID !== 'YOUR_GA4_MEASUREMENT_ID') {
    window.gtag('config', GA_MEASUREMENT_ID, {
      send_page_view: true,
      cookie_flags: 'SameSite=None;Secure'
    });
  }
};

// 페이지뷰 추적
export const logPageView = (url) => {
  if (typeof window.gtag === 'function' && GA_MEASUREMENT_ID) {
    // 페이지뷰 이벤트만 전송 (config 재설정 X)
    window.gtag('event', 'page_view', {
      page_path: url,
      page_location: window.location.href,
      page_title: document.title
    });
  }
};

// 이벤트 추적
export const logEvent = (action, category, label, value) => {
  if (typeof window.gtag === 'function') {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
    });
  }
};

// 사용자 정의 이벤트
export const trackEvent = {
  // 음성 녹음 이벤트
  startRecording: () => logEvent('start_recording', 'Voice', 'Recording Started'),
  stopRecording: () => logEvent('stop_recording', 'Voice', 'Recording Stopped'),
  
  // AI 처리 이벤트
  transcriptionComplete: (duration) => logEvent('transcription_complete', 'AI', 'STT Complete', duration),
  imageGenerated: () => logEvent('image_generated', 'AI', 'Image Generated'),
  
  // 사용자 상호작용
  saveImage: () => logEvent('save_image', 'User Action', 'Image Saved'),
  shareStory: () => logEvent('share_story', 'User Action', 'Story Shared'),
  
  // 컨설팅 관련 이벤트
  startConsulting: () => logEvent('start_consulting', 'Consulting', 'New Session Started'),
  continueConsulting: (sessionId) => logEvent('continue_consulting', 'Consulting', 'Session Continued', sessionId),
  
  // 문서 관련 이벤트
  viewDocument: (docType, docId) => logEvent('view_document', 'Document', docType, { document_id: docId }),
  deleteDocument: (docType, docId) => logEvent('delete_document', 'Document', docType, { document_id: docId }),
  downloadDocument: (docType, format) => logEvent('download_document', 'Document', `${docType}_${format}`),
  
  // 이력서 편집 관련 이벤트
  startEditingResume: (resumeId) => logEvent('start_editing_resume', 'Resume', 'Edit Started', { resume_id: resumeId }),
  saveResumeEdit: (resumeId, editedFields) => logEvent('save_resume_edit', 'Resume', 'Edit Saved', { 
    resume_id: resumeId,
    edited_fields: editedFields ? editedFields.join(',') : 'unknown'
  }),
  cancelResumeEdit: (resumeId) => logEvent('cancel_resume_edit', 'Resume', 'Edit Cancelled', { resume_id: resumeId }),
  
  // 영어 이력서 관련 이벤트
  generateEnglishResume: (resumeId) => logEvent('generate_english_resume', 'Resume', 'English Version Generated', { resume_id: resumeId }),
  viewEnglishResume: (resumeId) => logEvent('view_english_resume', 'Resume', 'English Version Viewed', { resume_id: resumeId }),
  switchLanguage: (resumeId, language) => logEvent('switch_resume_language', 'Resume', `Switch to ${language}`, { 
    resume_id: resumeId,
    language: language
  }),
  
  // 포트폴리오 관련 이벤트
  viewPortfolio: (portfolioId) => logEvent('view_portfolio', 'Portfolio', 'View', { portfolio_id: portfolioId }),
  publishPortfolio: (portfolioId) => logEvent('publish_portfolio', 'Portfolio', 'Publish', { portfolio_id: portfolioId }),
  
  // 에러 추적
  error: (errorType, errorMessage) => logEvent('exception', 'Error', errorType, {
    description: errorMessage,
    fatal: false
  })
};

// 사용자 속성 설정
export const setUserProperties = (properties) => {
  if (typeof window.gtag === 'function') {
    window.gtag('set', { user_properties: properties });
  }
};

// 전환 추적 (Google Ads)
export const trackConversion = (conversionLabel, value = null) => {
  if (typeof window.gtag === 'function') {
    const conversionData = {
      'send_to': `${GOOGLE_ADS_ID}/${conversionLabel}`,
      'currency': 'KRW'
    };
    
    if (value !== null) {
      conversionData.value = value;
    }
    
    window.gtag('event', 'conversion', conversionData);
  }
};

// 주요 전환 이벤트
export const trackGoogleAdsConversion = {
  // 회원가입 완료
  signupComplete: () => trackConversion('signup_complete'),
  
  // 컨설팅 시작
  consultingStart: () => trackConversion('consulting_start'),
  
  // 이력서 생성 완료
  resumeGenerated: () => trackConversion('resume_generated'),
  
  // 프로모 코드 사용
  promoCodeUsed: (value) => trackConversion('promo_code_used', value),
  
  // 프로모 코드 요청 (신규 추가)
  promoCodeRequested: () => {
    // Google Ads 전환 추적
    trackConversion('promo_code_requested');
    
    // 추가로 커스텀 이벤트도 전송
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'promo_request_submitted', {
        'event_category': 'engagement',
        'event_label': 'promo_request',
        'value': 1
      });
    }
  },
  
  // 포트폴리오 생성
  portfolioCreated: () => trackConversion('portfolio_created')
};
